import { useEffect, useRef, useState } from "react"
import type {
  ColumnSort,
  OnChangeFn,
  SortingState,
} from "@tanstack/react-table"
import { useNavigate } from "@/v2-backoffice-client/utils"

const EMPTY_SORTING: SortingState = []

/**
 * The controlled-sorting props produced by `useTableSortUrl`. Spread them
 * into `<Table>` (or thread them through a wrapper component's props down
 * to its `<Table>`).
 */
export type TableSortUrlProps = {
  initialSorting: SortingState
  sorting: SortingState
  onSortingChange: OnChangeFn<SortingState>
}

/** Reads the current value of a search param without subscribing to anything. */
function readSortValue(param: string) {
  return new URLSearchParams(window.location.search).get(param)
}

/** `-SharingRisk` → `{ id: "SharingRisk", desc: true }`; empty/garbage → null. */
function parseSortValue(value: string | null): ColumnSort | null {
  if (!value) return null
  const desc = value.startsWith("-")
  const id = desc ? value.slice(1) : value
  if (!id) return null
  return { id, desc }
}

function serializeSortEntry(entry: ColumnSort) {
  return `${entry.desc ? "-" : ""}${entry.id}`
}

function sortingEquals(a: SortingState, b: SortingState) {
  return (
    a.length === b.length &&
    a.every((entry, i) => entry.id === b[i].id && entry.desc === b[i].desc)
  )
}

export type UseTableSortUrlInput = {
  /**
   * Same id the table passes as `tableId`. The URL param is `${tableId}-sort`,
   * so multiple URL-sorted tables (tabs, sheets over list pages, side-by-side
   * panels) can never collide by construction.
   */
  tableId: string
  /**
   * Sorting applied when the URL carries no sort param. Also what the
   * toolbar "reset" restores. Treated as a mount-time constant.
   */
  initialSorting?: SortingState
}

/**
 * Makes a table's sort bookmarkable: the primary sort column is mirrored to a
 * per-table URL search param (`?<tableId>-sort=-SharingRisk`, `-` prefix =
 * descending) and restored from it on mount. Returns controlled-sorting props
 * to spread into `<Table>`.
 *
 * Contract:
 *
 * - The full `SortingState` (including multi-sort tiers, when the operator has
 *   the multi-sort setting on) lives in local state; the URL only carries the
 *   first tier. Restoring a URL therefore restores the primary sort, which
 *   becomes tier 1 of any multi-sort built on top of it.
 * - An absent param means "the default": the param is removed whenever sorting
 *   returns to `initialSorting` (or to unsorted), so default views keep
 *   canonical URLs and toolbar reset leaves no residue.
 * - URL writes use `replace` — sorting never pollutes browser history.
 *
 * Performance is structural, not memoized: the hook subscribes to no router
 * state. It reads the URL once on mount, writes on sort changes, and listens
 * to `popstate` for back/forward — the only way this table's namespaced param
 * can change externally while mounted (in-app navigation helpers all preserve
 * unrelated params, and nothing else writes this param). Unrelated URL churn
 * (view sheets opening, filter pills, tab switches) therefore causes zero
 * re-renders of the table.
 */
export function useTableSortUrl(
  input: UseTableSortUrlInput,
): TableSortUrlProps {
  const { tableId, initialSorting = EMPTY_SORTING } = input
  const param = `${tableId}-sort`

  const initialSortingRef = useRef(initialSorting)

  const [sorting, setSorting] = useState<SortingState>(() => {
    const entry = parseSortValue(readSortValue(param))
    return entry ? [entry] : initialSortingRef.current
  })

  const navigate = useNavigate()

  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === "function" ? updater(sorting) : updater
    setSorting(next)

    const isDefault =
      next.length === 0 || sortingEquals(next, initialSortingRef.current)
    const value = isDefault ? null : serializeSortEntry(next[0])
    if (value === readSortValue(param)) return

    const params = new URLSearchParams(window.location.search)
    if (value === null) {
      params.delete(param)
    } else {
      params.set(param, value)
    }
    const query = params.toString()
    navigate(
      `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`,
      { replace: true },
      false,
      false,
      true,
    )
  }

  // Back/forward can restore a history entry whose sort param differs from
  // the live state (sort writes use `replace`, but other features push
  // entries — e.g. opening a view sheet). Reconcile: the URL's entry becomes
  // tier 1, remaining multi-sort tiers are kept.
  useEffect(() => {
    function reconcileFromUrl() {
      const entry = parseSortValue(readSortValue(param))
      setSorting((current) => {
        const head = current[0]
        if (entry && head && entry.id === head.id && entry.desc === head.desc) {
          return current
        }
        if (!entry) {
          const isAtDefault =
            current.length === 0 ||
            sortingEquals(current, initialSortingRef.current)
          return isAtDefault ? current : initialSortingRef.current
        }
        return [
          entry,
          ...current.slice(1).filter((tier) => tier.id !== entry.id),
        ]
      })
    }

    window.addEventListener("popstate", reconcileFromUrl)
    return () => window.removeEventListener("popstate", reconcileFromUrl)
  }, [param])

  return { initialSorting: initialSortingRef.current, sorting, onSortingChange }
}
